import { useState } from 'react';
import { Grid, IconButton, Typography } from '@mui/material';
import { FiEdit } from 'react-icons/fi';
import { BsArrowRight } from 'react-icons/bs';
import { useNavigate, useParams } from 'react-router';
import StyledDivider from 'src/components/divider/divider';
import Tooltip from '@mui/material/Tooltip';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
  IScenarioSummaryStrip,
  Scenario
} from '../../../scenarios/types/scenario.model';
import CreateNewScenarioDialog from '../create-scenario-dialog/create-scenario-dialog';
import { setScenario } from '../../store/simulator-home-slice';
import ScenarioCreateSuccessDialog from '../scenario-create-success/scenario-create-success-dialog';

interface ICurrentScenarioInfoCardProps {
  scenarioName: string;
  scenarioSummaryStrip: IScenarioSummaryStrip;
  filters: {
    field_name: string;
    field_alias: string;
    field_values: string[];
  }[];
}

export default function CurrentScenarioInfoCard({
  scenarioName,
  scenarioSummaryStrip,
  filters
}: ICurrentScenarioInfoCardProps) {
  const { id: scenarioId } = useParams();
  const appDispatch = useAppDispatch();
  const [isUpdateScenarioDialogOpen, setIsUpdateScenarioDialogOpen] =
    useState(false);
  const [isScenarioUpdatedSuccessfully, setScenarioUpdatedSuccessfully] =
    useState(false);

  const { currentScenario } = useAppSelector((state) => state.simulatorHome);
  const navigate = useNavigate();
  const summary: {
    title: string;
    value: keyof IScenarioSummaryStrip;
    color: string;
  }[] = [
    {
      title: 'Innovation',
      value: 'n_innovated',
      color: '#393939'
    },
    {
      title: 'Delisted',
      value: 'n_delisted',
      color: '#393939'
    },
    {
      title: 'Pricing & Distribution',
      value: 'n_pricing',
      color: '#80EDFC'
    },
    {
      title: 'Renovation',
      value: 'n_renovated',
      color: '#EA9988'
    }
  ];

  const onEditClick = () => {
    setIsUpdateScenarioDialogOpen(true);
  };

  const handleUpdateSuccessDialogClose = () => {
    setScenarioUpdatedSuccessfully(false);
  };

  const updateScenarioClickHandler = (scenario: Scenario) => {
    setIsUpdateScenarioDialogOpen(false);
    if (scenario) {
      setScenarioUpdatedSuccessfully(true);
      appDispatch(setScenario(scenario));
    }
  };

  const handleContinueClick = () => {
    const activeScenarioId = scenarioId || currentScenario?.pk_id;
    if (!activeScenarioId){
      return;
    }
    const plannerIncluded = currentScenario?.planner_included ?? true;
    const moduleRoute = plannerIncluded ? 'planner': 'innovation';
    navigate(`/srm/simulator/${activeScenarioId}/${moduleRoute}`);
  };

  return (
    <>
      <Grid
        container
        sx={{ backgroundColor: '#FCF1E8', p: 1, maxWidth: '700px' }}
      >
        <Grid
          container
          item
          alignItems='center'
          justifyContent='space-between'
          xs={12}
        >
          <Grid container alignItems='center' item flexWrap='nowrap' xs='auto'>
            <Typography
              component='span'
              style={{
                lineHeight: '1.5em',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
                width: '90%'
              }}
              sx={{ fontSize: '12px', fontWeight: 700, mr: 1 }}
            >
              <Tooltip title={scenarioName} arrow>
                <span>{scenarioName}</span>
              </Tooltip>
            </Typography>
            <IconButton aria-label='delete' size='small'>
              <FiEdit style={{ fontSize: '12px' }} onClick={onEditClick} />
            </IconButton>
          </Grid>
          <Grid
            container
            sx={{ cursor: 'pointer' }}
            onClick={handleContinueClick}
            alignItems='center'
            item
            xs='auto'
          >
            <Typography
              component='span'
              sx={{
                fontSize: '12px',
                fontWeight: 700,
                color: 'primary.main',
                mr: 1
              }}
            >
              Continue
            </Typography>
            <BsArrowRight style={{ fontSize: '12px' }} />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        {filters.map((filter) => (
          <Grid
            item
            xs={12}
            container
            alignItems='center'
            key={filter.field_name}
          >
            <Typography component='span' sx={{ fontSize: '12px', mr: 1 }}>
              {filter.field_alias}
            </Typography>
            -
            <Typography
              component='span'
              sx={{ fontSize: '12px', ml: 1, fontWeight: 700 }}
            >
              {filter.field_values.join(', ')}
            </Typography>
          </Grid>
        ))}
        <Grid item xs={12}>
          <StyledDivider />
        </Grid>
        <Grid container spacing={1} item xs={12}>
          {summary.map((item) => (
            <Grid key={item.title} item xs>
              <Grid
                sx={{
                  backgroundColor: 'white',
                  p: 1,
                  borderRadius: (theme) => theme.spacing(0.5)
                }}
              >
                <Typography sx={{ fontSize: '10px' }}>{item.title}</Typography>
                <Typography
                  sx={{ fontSize: '16px', color: item.color, fontWeight: 700 }}
                >
                  {scenarioSummaryStrip && scenarioSummaryStrip[item.value]
                    ? scenarioSummaryStrip[item.value]
                    : '-'}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Grid>
      {isUpdateScenarioDialogOpen ? (
        <CreateNewScenarioDialog
          isEditable
          isDuplicate={false}
          currentScenario={currentScenario}
          isOpen={isUpdateScenarioDialogOpen}
          onClose={updateScenarioClickHandler}
        />
      ) : null}
      {isScenarioUpdatedSuccessfully ? (
        <ScenarioCreateSuccessDialog
          imagePath='/static/images/success.svg'
          isOpen={isScenarioUpdatedSuccessfully}
          onClose={handleUpdateSuccessDialogClose}
          message='Scenario Updated Successfully'
        />
      ) : null}
    </>
  );
}
